import React, { useEffect, useRef } from 'react'
import { useLabraChatSelector } from '../../../modules/labraChat/context'
import {
  selectLabraChatMessages,
  selectIsLabraChatFullScreen,
} from '../../../modules/labraChat/selectors'
import { UserBubble } from './UserBubble'
import { AssistantBubble } from './AssistantBubble'
import { LABRA_CHAT_SCROLL_ID } from '../constants'
import { useStyles } from './ChatThread.styles'
import { formatDaySeparator, isSameCalendarDay } from './ChatThread.helpers'
import { useUserDisplayTimeZone } from './useUserDisplayTimeZone'

export const ChatThread: React.FC = () => {
  const { classes } = useStyles()
  const scrollAnchorRef = useRef<HTMLDivElement>(null)
  const isAnchorVisibleRef = useRef(true)
  // Set when the user manually scrolls up mid-stream; pauses auto-follow until
  // they return to the bottom. Kept separate from `isAnchorVisibleRef` because
  // the observer is async — reading its value inside a sync scroll handler races
  // with the streaming effect and causes the snap-back flicker.
  const userPausedFollowRef = useRef(false)
  const previousMessageCountRef = useRef(0)

  const messages = useLabraChatSelector(selectLabraChatMessages)
  const userTimezone = useUserDisplayTimeZone()
  // The compact and full-screen layouts mount different scroll containers under
  // the same id, so the observer must re-bind when the layout switches.
  const isFullScreen = useLabraChatSelector(selectIsLabraChatFullScreen)

  // Track whether the bottom of the thread is in view, and let a manual scroll
  // gesture pause auto-follow. Visibility comes from IntersectionObserver (no
  // pixel math); the pause comes from wheel/touch *direction*, which is
  // synchronous and never fires for programmatic scrollIntoView — so the two
  // sources never race.
  useEffect(() => {
    const anchor = scrollAnchorRef.current
    const root = document.getElementById(LABRA_CHAT_SCROLL_ID)
    if (!anchor) return

    const observer = new IntersectionObserver(
      entries => {
        const atBottom = entries[0]?.isIntersecting ?? true
        isAnchorVisibleRef.current = atBottom
        // Returning to the bottom resumes following the stream.
        if (atBottom) userPausedFollowRef.current = false
      },
      { root: root ?? null }
    )
    observer.observe(anchor)

    // A wheel-up gesture means the user wants to read earlier content.
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) userPausedFollowRef.current = true
    }
    // Touch: a downward finger drag scrolls content up — same intent.
    let lastTouchY = 0
    const onTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches[0]?.clientY ?? 0
    }
    const onTouchMove = (event: TouchEvent) => {
      const y = event.touches[0]?.clientY ?? 0
      if (y > lastTouchY) userPausedFollowRef.current = true
      lastTouchY = y
    }

    root?.addEventListener('wheel', onWheel, { passive: true })
    root?.addEventListener('touchstart', onTouchStart, { passive: true })
    root?.addEventListener('touchmove', onTouchMove, { passive: true })

    return () => {
      observer.disconnect()
      root?.removeEventListener('wheel', onWheel)
      root?.removeEventListener('touchstart', onTouchStart)
      root?.removeEventListener('touchmove', onTouchMove)
    }
  }, [isFullScreen])

  // Pin to the newest content: always when a new message is sent/received, and
  // during streaming only if the user is at the bottom and hasn't scrolled up.
  // Instant scroll — 'smooth' restarts an animation every frame and stutters.
  useEffect(() => {
    const anchor = scrollAnchorRef.current
    if (!anchor) return
    const isNewMessage = messages.length > previousMessageCountRef.current
    previousMessageCountRef.current = messages.length
    // A brand-new message always pins to the bottom and resumes following.
    if (isNewMessage) userPausedFollowRef.current = false
    if (
      isNewMessage ||
      (isAnchorVisibleRef.current && !userPausedFollowRef.current)
    ) {
      anchor.scrollIntoView({ block: 'end' })
    }
  }, [messages])

  return (
    <div className={classes.root}>
      {messages.map((message, index) => {
        const previous = messages[index - 1]
        // Open a new day group whenever the calendar day changes (and always
        // before the very first message).
        const showDaySeparator =
          !previous ||
          !isSameCalendarDay(
            previous.timestamp,
            message.timestamp,
            userTimezone
          )
        return (
          <React.Fragment key={message.id}>
            {showDaySeparator && (
              <div className={classes.daySeparator}>
                <span className={classes.daySeparatorLabel}>
                  {formatDaySeparator(message.timestamp, userTimezone)}
                </span>
              </div>
            )}
            {message.role === 'USER' ? (
              <UserBubble message={message} />
            ) : (
              <AssistantBubble message={message} />
            )}
          </React.Fragment>
        )
      })}
      <div ref={scrollAnchorRef} className={classes.scrollAnchor} />
    </div>
  )
}
